// ---- the pack ----
// Everything about the sofra that belongs to one family rather than to the
// game: what she serves and what each dish is called, the letters those names
// spell and how each is said, every line the three of them have, where their
// three models are, and the script it is all written in. Danny's is
// tata/src/data.ts; another family's is f/<slug>/data.ts, with the same names
// exported (see f/README.md), and the game is the same code over either.
//
// It reaches the game the way the roster reaches the engine (setVoice,
// setRound): the entry point hands it over (play, game.ts) and nothing imports
// a data file but an entry point. Every module that wants a line reads it
// through pack() when it needs it, which is always inside a function — so
// nothing here runs before there is a pack to read.
//
// Since 2026-10-31 the entry point does not import the data file either: it
// fetches the pack as data, pack.json, which scripts/build-pack.mjs writes
// from data.ts (loadPack, below). A family's game is then the one tata/
// bundle over a file, and an app can hand the same file to the same bundle
// from wherever it has kept it (docs/plans/2026-10-31-mobile-apps.md). The
// data.ts stays: it is what the checks read and what the pack is built from.
import type {ScriptCfg} from '../../core/letters';
import type {PackFile} from '../../core/store';

// The boy's clips by what he does with them (boy.ts), for a model whose export
// named them differently from Danny's. Eating is the one he can do without:
// with no eat clip he eats it where it sits on the table.
export interface BoyClips{walk:string;run:string;idles:string[];eat?:string}
// His father's likewise (baba.ts): he only walks and stands. An idle longer
// than one of his pauses he stands on the first frame of, and now and then
// plays through.
export interface BabaClips{walk:string;idle:string}
export interface FoodSpec{
  w:string;          // the name, bare glyphs: what the round spells
  e:string;          // its emoji: the tray's question (game.ts), and the recording portal's card
  across:number;     // how wide it sits on the table, in the field's units
  file:string;       // its model, from the page
}
export interface TataPack{
  FOODS:FoodSpec[];
  NAMES:Record<string,string>;    // letter -> how it is said, for the robot voice when a take is missing
  DIA:Record<string,string>;      // word -> how it is said, likewise; in Arabic, also the حركات its letters are shown with
  MAXLEN:number;                  // for scripts/check-words.js; the game never reads it
  INTRO:string;                   // her hello
  MAKE:(w:string)=>string;        // hers as she serves a dish
  YUM:(w:string)=>string;         // his as he eats it
  MAKE_ANY:string[];              // ...and for a dish whose own take has not been made
  YUM_ANY:string[];
  SAHTEIN:string;                 // his father's one line
  // Where the three of them are, from the page. Danny's sit beside it; another
  // family's are their own, or Danny's borrowed (../../tata/tata.glb) until
  // theirs are made. Each comes rigged and clipped the way these three did
  // (core/humanoid.ts); a model that does not is a change to this, not a row.
  // A boy or a father whose clips are named otherwise says what they are
  // called beside him.
  CAST:{tata:string;boy:string;baba:string;boyClips?:BoyClips;babaClips?:BabaClips};
  // And her table, likewise from the page (set.ts draws it to the size the
  // game needs, whatever size it was made at).
  TABLE:string;
  SCRIPT?:ScriptCfg;              // Arabic when absent (core/letters.ts)
  // How the child makes a word unless the address says otherwise (game.ts):
  // spelled from the letters in the sky, or its first letter traced. 'spell'
  // when absent.
  MODE?:'spell'|'trace';
  // Where the tracing board (core/trace.ts) borrows the syllables it
  // says that no word here is spelled with — the bare क of के, the مَ of مَوْز —
  // by path from the page: vowels/ has every consonant with every sign.
  SYLLABLES?:string;
  // Where its clips are, when they are not audio/ beside the page: a pack
  // loaded from a file says so from the site's root (core/assets.ts), since
  // the page it is played on may be anybody's.
  AUDIO?:string;
  // And what its own page says that the game's page does not: its language
  // and direction, its title, the hint over the tray, the face the tray is
  // drawn in, and its home-screen icon (a root path, listed in files). Read
  // off the family's index.html by scripts/build-pack.mjs, and put on
  // whichever page plays the pack (main.ts), so an app that has only tata/'s
  // page shows their sofra as their own page would.
  PAGE?:PageCfg;
}
export interface PageCfg{lang:string;dir:'ltr'|'rtl';title:string;hint:string;font?:string;icon?:string}
let P:TataPack|null=null;
export const setPack=(p:TataPack)=>{P=p;};
export function pack(){if(!P)throw new Error('tata: setPack() has not been called');return P;}

// ---- the pack as a file ----
// pack.json, as scripts/build-pack.mjs writes it: the tables above with every
// path from the site's root and the two line-makers as tables (a function is
// not data), and `files`, everything the game will fetch for this pack, with
// its size and hash, which the website never reads and an app's store does
// (core/store.ts).
// The builder and this loader are the two ends of one format, so a change to
// either is a change to both; `pack` is its version.
export interface TataPackJson{
  pack:1;
  kind:'tata';
  id:string;                      // 'tata' for Danny's; a family's slug
  base:string;                    // its folder, from the site's root: '/tata/', '/f/<slug>/'
  data:Omit<TataPack,'MAKE'|'YUM'>&{MAKE:Record<string,string>;YUM:Record<string,string>};
  files:PackFile[];
}
const NEEDED:(keyof TataPackJson['data'])[]=['FOODS','NAMES','DIA','INTRO','MAKE','YUM','MAKE_ANY','YUM_ANY','SAHTEIN','CAST','TABLE','AUDIO'];
// A pack from its file: the tables as the game reads them. Throws, naming the
// file, on anything that is not a pack this bundle can play, so that a page
// handed the wrong file fails at open and not at the first round.
export async function loadPack(url:string):Promise<TataPack>{
  const r=await fetch(url); if(!r.ok)throw new Error(url+': '+r.status);
  const j=await r.json() as Partial<TataPackJson>;
  if(j.pack!==1||j.kind!=='tata'||!j.data)throw new Error(url+" is not a pack this game plays (pack 1, kind 'tata')");
  const d=j.data; for(const k of NEEDED)if(!(k in d))throw new Error(url+': the pack has no '+k);
  const {MAKE,YUM,...rest}=d;
  return {...rest,MAKE:w=>MAKE[w]??w,YUM:w=>YUM[w]??w};
}
